import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Zap, Loader2, AlertTriangle, Settings2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

import { BikePicker } from "./BikePicker";
import { HexDropzone } from "./HexDropzone";
import { PortPicker } from "./PortPicker";
import { LogConsole } from "./LogConsole";
import { StatusBadge } from "./StatusBadge";
import { AndroidFlashingGuide } from "./AndroidFlashingGuide";
import { parseIntelHex } from "../lib/intelHex";
import { getBikeById } from "../lib/bikes";
import {
    isWebSerialSupported,
    requestPort,
    getAuthorizedPorts,
    flashNano,
    startSerialLogger,
} from "../lib/stk500";

let logIdCounter = 0;

export default function FlasherApp() {
    const [supported, setSupported] = useState(true);
    const [isAndroid, setIsAndroid] = useState(false);
    const [file, setFile] = useState(null);
    const [selectedBikeId, setSelectedBikeId] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [port, setPort] = useState(null);
    const [status, setStatus] = useState("disconnected"); // disconnected | connected | flashing | done | error
    const [progress, setProgress] = useState({ pct: 0, page: 0, total: 0 });
    const [entries, setEntries] = useState([]);
    const [isFlashing, setIsFlashing] = useState(false);
    const [baudRate, setBaudRate] = useState(115200);
    const [serialLogging, setSerialLogging] = useState(false);
    const parsedRef = useRef(null);
    const serialLoggerRef = useRef(null);
    const serialLineRef = useRef("");

    const addLog = useCallback((level, msg) => {
        setEntries((prev) => {
            const next = [
                ...prev,
                { id: ++logIdCounter, ts: Date.now(), level, msg },
            ];
            // Cap at 1000 entries to keep UI performant
            return next.length > 1000 ? next.slice(next.length - 1000) : next;
        });
    }, []);

    useEffect(() => {
        setSupported(isWebSerialSupported());
        setIsAndroid(/Android/i.test(navigator.userAgent));
    }, []);

    useEffect(() => {
        let active = true;

        const restoreAuthorizedDevice = async () => {
            try {
                const ports = await getAuthorizedPorts();
                // Choosing automatically is only safe when there is no ambiguity.
                if (active && ports.length === 1) {
                    setPort(ports[0]);
                    setStatus("connected");
                    addLog("ok", "Previously approved serial device selected automatically");
                }
            } catch (error) {
                // The manual device picker remains available if the browser denies this lookup.
            }
        };

        restoreAuthorizedDevice();
        return () => {
            active = false;
        };
    }, [addLog]);

    useEffect(() => () => {
        serialLoggerRef.current?.close();
    }, []);

    const acceptFirmware = useCallback(
        async (f, err, source) => {
            if (err) {
                toast.error(err);
                addLog("error", err);
                parsedRef.current = null;
                setFile(null);
                return;
            }
            try {
                const text = await f.text();
                const parsed = parseIntelHex(text);
                parsedRef.current = parsed;
                setFile(f);
                addLog(
                    "ok",
                    `${source} loaded · ${parsed.totalBytes} bytes · max address 0x${parsed.maxAddress
                        .toString(16)
                        .padStart(4, "0")}`,
                );
            } catch (e) {
                parsedRef.current = null;
                setFile(null);
                toast.error(`Firmware parse failed: ${e.message}`);
                addLog("error", `Firmware parse failed: ${e.message}`);
            }
        },
        [addLog],
    );

    const handleBikeFirmware = useCallback(
        (f, err) => {
            const bike = getBikeById(selectedBikeId);
            const label = bike ? `Firmware for ${bike.name}` : "Firmware";
            acceptFirmware(f, err, label);
        },
        [acceptFirmware, selectedBikeId],
    );

    const handleManualFile = useCallback(
        (f, err) => acceptFirmware(f, err, `Custom firmware (${f?.name || "?"})`),
        [acceptFirmware],
    );

    const handleBikeSelected = (bikeId) => {
        setSelectedBikeId(bikeId);
        // Wipe any previously loaded firmware until the new one finishes loading
        parsedRef.current = null;
        setFile(null);
    };

    const handleClearBike = () => {
        setSelectedBikeId(null);
        parsedRef.current = null;
        setFile(null);
        addLog("info", "Bike selection cleared");
    };

    const handleClearManualFile = () => {
        parsedRef.current = null;
        setFile(null);
    };

    const handleRequestPort = async () => {
        try {
            const p = await requestPort();
            setPort(p);
            setStatus("connected");
            const info = p.getInfo();
            addLog(
                "ok",
                `Device connected · VID 0x${(info.usbVendorId || 0)
                    .toString(16)
                    .padStart(4, "0")} PID 0x${(info.usbProductId || 0)
                    .toString(16)
                    .padStart(4, "0")}`,
            );
        } catch (e) {
            if (e && e.name === "NotFoundError") {
                addLog("info", "Device selection cancelled");
            } else {
                toast.error(e.message || "Failed to select device");
                addLog("error", e.message || String(e));
            }
        }
    };

    const handleClearPort = () => {
        serialLoggerRef.current?.close();
        serialLoggerRef.current = null;
        setSerialLogging(false);
        setPort(null);
        setStatus("disconnected");
        addLog("info", "Device disconnected");
    };

    const handleToggleSerialLogger = async () => {
        if (!port || isFlashing) return;
        if (serialLoggerRef.current) {
            await serialLoggerRef.current.close();
            serialLoggerRef.current = null;
            setSerialLogging(false);
            return;
        }

        try {
            serialLineRef.current = "";
            const logger = await startSerialLogger(port, {
                baudRate,
                onStatus: (msg) => addLog("info", msg),
                onData: (chunk) => {
                    const lines = (serialLineRef.current + chunk).split(/\r?\n/);
                    serialLineRef.current = lines.pop() || "";
                    lines.forEach((line) => addLog("serial", line || " "));
                },
            });
            serialLoggerRef.current = logger;
            setSerialLogging(true);
        } catch (e) {
            toast.error(e.message || "Failed to start serial logger");
            addLog("error", e.message || String(e));
        }
    };

    const handleFlash = async () => {
        if (!file || !parsedRef.current || !port || isFlashing || serialLogging) return;
        setIsFlashing(true);
        setStatus("flashing");
        setProgress({ pct: 0, page: 0, total: 0 });
        const bike = getBikeById(selectedBikeId);
        addLog(
            "info",
            bike
                ? `Starting firmware update for ${bike.name}`
                : `Starting firmware update`,
        );

        try {
            await flashNano(port, parsedRef.current.buffer, {
                onProgress: (pct, page, total) =>
                    setProgress({ pct, page, total }),
                onLog: (level, msg) => addLog(level, msg),
                baudRate,
            });
            setStatus("done");
            toast.success("Firmware update complete!");
        } catch (e) {
            setStatus("error");
            addLog("error", e.message || String(e));
            toast.error(e.message || "Firmware update failed");
        } finally {
            setIsFlashing(false);
        }
    };

    const canFlash = useMemo(
        () => !!file && !!port && !isFlashing && !serialLogging && supported,
        [file, port, isFlashing, serialLogging, supported],
    );

    return (
        <div className="grain min-h-screen relative z-0">
            {/* Header */}
            <header className="bg-[#0d0d0e]/95 backdrop-blur-sm border-b border-[#27272a] py-4 px-6 md:px-8 flex justify-between items-center sticky top-0 z-50">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center border border-[#F59E0B] bg-[#F59E0B]/10">
                        <Zap className="w-4 h-4 text-[#F59E0B]" />
                    </div>
                    <div>
                        <div className="text-sm font-bold tracking-tight text-[#ededed]">
                            HOLD MY THROTTLE
                        </div>
                        <div
                            className="text-[10px] font-mono text-[#a1a1aa] uppercase tracking-[0.25em] -mt-0.5"
                            data-testid="app-subtitle"
                        >
                            Firmware Tool
                        </div>
                    </div>
                </div>
                {!isAndroid && <StatusBadge status={status} />}
            </header>

            {/* Main grid */}
            <main
                data-testid="flasher-main"
                className="max-w-[1400px] w-full mx-auto p-4 md:p-8"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                    {/* Controls column */}
                    <section
                        className={`${isAndroid ? "lg:col-span-12 max-w-2xl mx-auto w-full" : "lg:col-span-4"} flex flex-col gap-8`}
                    >
                        {/* Bike select block */}
                        <div className="flex flex-col gap-3">
                            <div className="flex items-baseline justify-between">
                                <h2 className="text-xs uppercase tracking-[0.25em] font-semibold text-[#a1a1aa]">
                                    01 · Select Bike
                                </h2>
                                {file && selectedBikeId && (
                                    <span className="text-[10px] font-mono text-[#10B981] uppercase tracking-[0.2em]">
                                        ready
                                    </span>
                                )}
                            </div>
                            <BikePicker
                                selectedBikeId={selectedBikeId}
                                onBikeSelected={handleBikeSelected}
                                onFirmwareLoaded={handleBikeFirmware}
                                onClear={handleClearBike}
                                disabled={isFlashing}
                                loaded={!!file && !!selectedBikeId}
                            />

                            {isAndroid && (
                                <AndroidFlashingGuide
                                    selectedBikeId={selectedBikeId}
                                    firmwareReady={!!file && !!selectedBikeId}
                                />
                            )}

                            {/* Advanced: custom firmware */}
                            <div className={`mt-1 ${isAndroid ? "hidden" : "block"}`}>
                                <button
                                    type="button"
                                    data-testid="advanced-toggle"
                                    onClick={() => setShowAdvanced((v) => !v)}
                                    className="text-[10px] font-mono text-[#a1a1aa] hover:text-[#F59E0B] uppercase tracking-[0.2em] flex items-center gap-1.5 transition-colors"
                                >
                                    <Settings2 className="w-3 h-3" />
                                    Advanced
                                    <ChevronDown
                                        className={`w-3 h-3 transition-transform ${
                                            showAdvanced ? "rotate-180" : ""
                                        }`}
                                    />
                                </button>
                                {showAdvanced && (
                                    <div
                                        data-testid="advanced-panel"
                                        className="mt-3 flex flex-col gap-2"
                                    >
                                        <div className="text-[10px] font-mono text-[#a1a1aa] uppercase tracking-[0.15em]">
                                            Upload custom .hex file
                                        </div>
                                        <HexDropzone
                                            file={
                                                selectedBikeId ? null : file
                                            }
                                            onFileSelected={(f, err) => {
                                                // Manual upload overrides bike selection
                                                if (f) setSelectedBikeId(null);
                                                handleManualFile(f, err);
                                            }}
                                            onClear={handleClearManualFile}
                                            disabled={isFlashing}
                                        />
                                        <div className="text-[10px] font-mono text-[#52525b] leading-relaxed">
                                            Use this only if you have a custom
                                            firmware file. This will override the
                                            bike selection above.
                                        </div>
                                        <label className="mt-3 flex flex-col gap-1.5 text-[10px] font-mono text-[#a1a1aa] uppercase tracking-[0.15em]">
                                            Serial baud rate
                                            <select
                                                data-testid="baud-rate-select"
                                                value={baudRate}
                                                onChange={(event) => setBaudRate(Number(event.target.value))}
                                                disabled={isFlashing || serialLogging}
                                                className="bg-[#0d0d0e] border border-[#27272a] px-3 py-2 text-xs text-[#ededed] disabled:opacity-50"
                                            >
                                                <option value={115200}>115200 (default)</option>
                                                <option value={57600}>57600</option>
                                                <option value={38400}>38400</option>
                                                <option value={19200}>19200</option>
                                                <option value={9600}>9600</option>
                                            </select>
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Port block */}
                        <div
                            className={`flex-col gap-3 ${isAndroid ? "hidden" : "flex"}`}
                        >
                            <div className="flex items-baseline justify-between">
                                <h2 className="text-xs uppercase tracking-[0.25em] font-semibold text-[#a1a1aa]">
                                    02 · Connect Device
                                </h2>
                                {port && (
                                    <span className="text-[10px] font-mono text-[#10B981] uppercase tracking-[0.2em]">
                                        connected
                                    </span>
                                )}
                            </div>
                            <PortPicker
                                port={port}
                                onRequest={handleRequestPort}
                                onClear={handleClearPort}
                                onToggleLogger={handleToggleSerialLogger}
                                loggerActive={serialLogging}
                                disabled={isFlashing}
                                supported={supported}
                            />
                            {port && (
                                <div className={`text-[10px] font-mono uppercase tracking-[0.15em] ${serialLogging ? "text-[#10B981]" : "text-[#52525b]"}`}>
                                    {serialLogging ? `Serial logger active · ${baudRate} baud` : `Serial logger ready · ${baudRate} baud`}
                                </div>
                            )}
                        </div>

                        {/* Flash block */}
                        <div
                            className={`flex-col gap-3 ${isAndroid ? "hidden" : "flex"}`}
                        >
                            <h2 className="text-xs uppercase tracking-[0.25em] font-semibold text-[#a1a1aa]">
                                03 · Update Firmware
                            </h2>
                            <button
                                type="button"
                                data-testid="flash-button"
                                disabled={!canFlash}
                                onClick={handleFlash}
                                className={`w-full py-5 text-base font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-3 transition-all ${
                                    isFlashing
                                        ? "bg-[#3B82F6] text-white animate-pulse cursor-wait"
                                        : canFlash
                                          ? "bg-[#F59E0B] text-[#0d0d0e] hover:brightness-110"
                                          : "bg-[#27272a] text-[#52525b] cursor-not-allowed"
                                }`}
                            >
                                {isFlashing ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        UPDATING
                                    </>
                                ) : (
                                    <>
                                        <Zap className="w-5 h-5" />
                                        UPDATE FIRMWARE
                                    </>
                                )}
                            </button>

                            {/* Progress */}
                            <div
                                data-testid="flash-progress-bar"
                                className={`flex flex-col gap-2 transition-opacity ${
                                    isFlashing || status === "done"
                                        ? "opacity-100"
                                        : "opacity-50"
                                }`}
                            >
                                <div className="h-2 w-full bg-[#27272a] overflow-hidden">
                                    <div
                                        data-testid="flash-progress-indicator"
                                        className={`h-full transition-all duration-200 ${
                                            status === "done"
                                                ? "bg-[#10B981]"
                                                : status === "error"
                                                  ? "bg-[#ef4444]"
                                                  : "bg-[#3B82F6]"
                                        }`}
                                        style={{ width: `${progress.pct}%` }}
                                    />
                                </div>
                                <div className="text-[11px] font-mono flex justify-between text-[#a1a1aa] uppercase tracking-[0.15em]">
                                    <span>
                                        {progress.total > 0
                                            ? `block ${progress.page}/${progress.total}`
                                            : "block 0/0"}
                                    </span>
                                    <span data-testid="flash-progress-pct">
                                        {progress.pct}%
                                    </span>
                                </div>
                            </div>

                            {!supported && (
                                <div className="flex items-start gap-2 text-xs font-mono text-[#ef4444] mt-2">
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>
                                        Your browser does not support direct
                                        device communication. Please use Chrome
                                        or Edge on desktop.
                                    </span>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Terminal column */}
                    <section
                        className={`${isAndroid ? "hidden" : "flex"} lg:col-span-8 flex-col min-h-[520px] lg:min-h-[700px]`}
                    >
                        <div className="flex items-baseline justify-between mb-3">
                            <h2 className="text-xs uppercase tracking-[0.25em] font-semibold text-[#a1a1aa]">
                                Live Log
                            </h2>
                            <span className="text-[10px] font-mono text-[#52525b] uppercase tracking-[0.2em]">
                                {entries.length} entries
                            </span>
                        </div>
                        <LogConsole
                            entries={entries}
                            onClear={() => setEntries([])}
                        />
                    </section>
                </div>

                {/* Footer hint */}
                <div
                    className={`mt-8 text-[11px] font-mono text-[#52525b] leading-relaxed max-w-2xl ${isAndroid ? "hidden" : "block"}`}
                >
                    <span className="text-[#a1a1aa]">note</span> — this tool
                    updates your Hold My Throttle control module directly from
                    your browser over USB. Nothing leaves your computer. Make
                    sure your control module is plugged in before clicking
                    Connect Device.
                </div>
            </main>
        </div>
    );
}
